import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/dark.css";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast, { Toaster } from 'react-hot-toast';
import Pagination from '../components/Pagination';

const FieldWithAdd = ({ value, onChange, placeholder, type }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (type) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/reference-values?type=${type}`)
        .then(res => setOptions(res.data.map(item => item.value)))
        .catch(err => console.error(err));
    }
  }, [type]);

  const handleSave = async () => {
    if (newValue.trim()) {
      if (type) {
        try {
          await axios.post(`${process.env.REACT_APP_API_URL}/api/reference-values`, { type, value: newValue.trim() });
          setOptions(prev => prev.includes(newValue.trim()) ? prev : [...prev, newValue.trim()]);
        } catch (error) {
          console.error('Error saving reference value:', error);
        }
      }
      onChange({ target: { value: newValue.trim() } });
      setNewValue('');
      setIsModalOpen(false);
    }
  };

  return (
    <div className="flex w-full relative">
      <select 
        className="bg-slate-950 border border-slate-800 border-r-0 p-2 rounded-l-xl text-white text-sm w-full outline-none focus:border-emerald-500 appearance-none"
        value={value || ''}
        onChange={onChange}
      >
        <option value="" disabled hidden>Select {placeholder}</option>
        {options.map((opt, idx) => (
          <option key={idx} value={opt}>{opt}</option>
        ))}
        {value && !options.includes(value) && <option value={value}>{value}</option>}
      </select>
      <div className="absolute right-[45px] top-[10px] pointer-events-none">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
      </div>
      <button 
        type="button" 
        onClick={() => setIsModalOpen(true)}
        className="bg-emerald-500 hover:bg-emerald-400 text-white px-3 rounded-r-xl flex items-center justify-center font-bold text-xl transition-colors z-10"
        title={`Add new ${placeholder}`}
      >
        +
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-4">Add New {placeholder}</h3>
            <input 
              type="text"
              autoFocus
              className="bg-slate-950 border border-slate-700 p-3 rounded-xl text-white w-full mb-6 outline-none focus:border-emerald-500"
              placeholder={`Enter ${placeholder}`}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
            <div className="flex justify-end gap-3">
              <button 
                type="button"
                onClick={() => setIsModalOpen(false)} 
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={handleSave}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black rounded-xl font-bold text-sm transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SubAgentMaster = () => {
  const [agents, setAgents] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'form'
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Form State
  const [formData, setFormData] = useState({
    sub_agent_data_collection_date_and_time: '',
    sub_agent_code: '',
    sub_agent_name: '',
    sub_agent_discount_perc: '',
    sub_agent_address_line1: '',
    sub_agent_address_line2: '',
    sub_agent_address_line3: '',
    ref_country_id: '',
    ref_state_id: '',
    ref_district_id: '',
    sub_agent_pincode: '',
    sub_agent_data_source_others: '',
    status: 'Active'
  });

  const [contacts, setContacts] = useState([
    { contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }
  ]);

  const [emails, setEmails] = useState([
    { email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }
  ]);
  const [countriesList, setCountriesList] = useState([]);

  useEffect(() => {
    if (view === 'list') {
      fetchAgents();
    }
    axios.get(`${process.env.REACT_APP_API_URL}/api/countries`)
      .then(res => setCountriesList(res.data))
      .catch(err => console.error(err));
  }, [view]);

  const getMobileLengthByCode = (code) => {
    const country = countriesList.find(c => c.country_code === code);
    return country ? country.mobile_length : 10;
  };

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/sub_agents`, { withCredentials: true });
      setAgents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const searchStr = searchTerm.toLowerCase();
    return (
      (agent.sub_agent_name && agent.sub_agent_name.toLowerCase().includes(searchStr)) ||
      (agent.sub_agent_code && agent.sub_agent_code.toLowerCase().includes(searchStr)) ||
      (agent.status && agent.status.toLowerCase().includes(searchStr))
    );
  });

  const totalPages = Math.ceil(filteredAgents.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredAgents.slice(indexOfFirstRecord, indexOfLastRecord);

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredAgents.map((a, index) => ({
      "S.No": index + 1,
      "Sub Agent Code": a.sub_agent_code,
      "Sub Agent Name": a.sub_agent_name,
      "Collection Date": a.sub_agent_data_collection_date_and_time || '',
      "Discount %": a.sub_agent_discount_perc || '',
      "Status": a.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SubAgents");
    XLSX.writeFile(wb, "SubAgents_Export.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Sub Agent Directory", 14, 15);
    const tableColumn = ["S.No", "Sub Agent Code", "Sub Agent Name", "Collection Date", "Discount %", "Status"];
    const tableRows = [];
    filteredAgents.forEach((a, index) => {
      tableRows.push([
        index + 1,
        a.sub_agent_code, 
        a.sub_agent_name, 
        a.sub_agent_data_collection_date_and_time || '', 
        a.sub_agent_discount_perc || '', 
        a.status
      ]);
    });
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20
    });
    doc.save("SubAgents_Export.pdf");
  };


  const handleEdit = async (id) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/sub_agents/${id}`, { withCredentials: true });
      const agent = res.data;
      setEditId(id);
      setFormData({
        sub_agent_data_collection_date_and_time: agent.sub_agent_data_collection_date_and_time || '',
        sub_agent_code: agent.sub_agent_code || '',
        sub_agent_name: agent.sub_agent_name || '',
        sub_agent_discount_perc: agent.sub_agent_discount_perc || '',
        sub_agent_address_line1: agent.sub_agent_address_line1 || '',
        sub_agent_address_line2: agent.sub_agent_address_line2 || '',
        sub_agent_address_line3: agent.sub_agent_address_line3 || '',
        ref_country_id: agent.ref_country_id || '',
        ref_state_id: agent.ref_state_id || '',
        ref_district_id: agent.ref_district_id || '',
        sub_agent_pincode: agent.sub_agent_pincode || '',
        sub_agent_data_source_others: agent.sub_agent_data_source_others || '',
        status: agent.status || 'Active'
      });
      setContacts(agent.contacts.length ? agent.contacts.map(c => ({...c, primary_contact: !!c.primary_contact, contact_country_code: c.contact_country_code || '+91'})) : [{ contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }]);
      setEmails(agent.emails.length ? agent.emails.map(e => ({...e, primary_contact: !!e.primary_contact})) : [{ email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }]);
      setView('form');
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = (id) => {
    setItemToDelete(id);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/sub_agents/${itemToDelete}`, { withCredentials: true });
      fetchAgents();
      toast.success('Sub Agent deleted successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Error deleting sub agent.');
    } finally {
      setItemToDelete(null);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setFormErrors({});
    setFormData({
      sub_agent_data_collection_date_and_time: '', sub_agent_code: '', sub_agent_name: '', sub_agent_discount_perc: '', sub_agent_address_line1: '', sub_agent_address_line2: '', sub_agent_address_line3: '', ref_country_id: '', ref_state_id: '', ref_district_id: '', sub_agent_pincode: '', sub_agent_data_source_others: '', status: 'Active'
    });
    setContacts([{ contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }]);
    setEmails([{ email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.sub_agent_name.trim()) errors.sub_agent_name = "Sub Agent Name is required";
    
    contacts.forEach((contact, index) => {
      const length = getMobileLengthByCode(contact.contact_country_code || '+91');
      const regex = new RegExp(`^\\d{${length}}$`);
      if (contact.contact_number && !regex.test(contact.contact_number)) {
        errors[`contact_${index}_number`] = `Mobile number must be exactly ${length} digits`;
      }
    });

    emails.forEach((email, index) => {
      if (email.email_id && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.email_id)) {
        errors[`email_${index}_id`] = "Invalid email format";
      }
    });

    if (formData.sub_agent_pincode && !/^\d{6}$/.test(formData.sub_agent_pincode)) {
      errors.sub_agent_pincode = "Pincode must be exactly 6 digits";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please fix the validation errors.');
      return;
    }
    setFormErrors({});

    try {
      const payload = { ...formData, contacts, emails };
      if (editId) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/sub_agents/${editId}`, payload, { withCredentials: true });
        toast.success('Sub Agent updated successfully!');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/sub_agents`, payload, { withCredentials: true });
        toast.success('Sub Agent created successfully!');
      }
      setView('list');
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error('Error saving Sub Agent. Please check the fields.');
    }
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...contacts];
    newContacts[index][field] = value;
    if (field === 'primary_contact' && value) {
      newContacts.forEach((c, i) => { if (i !== index) c.primary_contact = false; });
    }
    setContacts(newContacts);
  };

  const addContact = () => {
    setContacts([...contacts, { contact_person: '', ref_designation_id: '', ref_contact_number_type_id: '', contact_country_code: '+91', contact_number: '', contact_extension: '', contact_timing_from: '', contact_timing_to: '', primary_contact: false }]);
  };

  const removeContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index, field, value) => {
    const newEmails = [...emails];
    newEmails[index][field] = value;
    if (field === 'primary_contact' && value) {
      newEmails.forEach((e, i) => { if (i !== index) e.primary_contact = false; });
    }
    setEmails(newEmails);
  };

  const addEmail = () => {
    setEmails([...emails, { email_id: '', contact_person: '', ref_designation_id: '', primary_contact: false }]);
  };

  const removeEmail = (index) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  return (
    <Layout title="Sub Agent Master" subtitle="Manage sub agents and their contacts">
      <Toaster />
      {view === 'list' ? (
        <div className="bg-slate-900/20 border border-slate-900 rounded-3xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-white text-lg">Sub Agent Directory</h3>
            <div className="flex gap-3">
              <input 
                type="text" 
                placeholder="Search sub agents..." 
                className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm outline-none focus:border-emerald-500"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
              <button onClick={exportToExcel} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl font-bold text-sm transition-colors">Excel</button>
              <button onClick={exportToPDF} className="bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-xl font-bold text-sm transition-colors">PDF</button>
              <button onClick={() => { resetForm(); setView('form'); }} className="bg-emerald-500 hover:bg-emerald-400 text-black px-4 py-2 rounded-xl font-bold text-sm transition-colors">Add Sub Agent</button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm text-gray-300">
              <thead>
                <tr className="border-b border-slate-800 text-gray-500 font-mono whitespace-nowrap">
                  <th className="pb-3 pr-4">S.No</th>
                  <th className="pb-3 pr-4">Sub Agent Code</th>
                  <th className="pb-3 pr-4">Sub Agent Name</th>
                  <th className="pb-3 pr-4">Collection Date</th>
                  <th className="pb-3 pr-4">Discount %</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {currentRecords.map((agent, index) => (
                  <tr key={agent.id} className="hover:bg-slate-800/20">
                    <td className="py-4 text-gray-400 pr-4">{indexOfFirstRecord + index + 1}</td>
                    <td className="py-4 font-mono text-emerald-400 pr-4">{agent.sub_agent_code}</td>
                    <td className="py-4 font-semibold text-white pr-4">{agent.sub_agent_name}</td>
                    <td className="py-4 pr-4">{agent.sub_agent_data_collection_date_and_time || '-'}</td>
                    <td className="py-4 pr-4">{agent.sub_agent_discount_perc || '-'}</td>
                    <td className="py-4 pr-4"><span className={`px-2 py-1 rounded-md text-xs ${agent.status === 'Active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>{agent.status}</span></td>
                    <td className="py-4 text-right min-w-[100px]">
                      <div className="flex justify-end gap-3">
                        <button onClick={() => handleEdit(agent.id)} title="Edit Sub Agent" className="text-blue-400 hover:text-blue-300 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                        </button>
                        <button onClick={() => handleDelete(agent.id)} title="Delete Sub Agent" className="text-rose-500 hover:text-rose-400 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {currentRecords.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-8 text-gray-500">No sub agents found matching your search.</td></tr>
                )}
              </tbody>
            </table>
            <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} totalItems={filteredAgents.length} />
          </div>
        </div>
      ) : (
        <div className="bg-slate-900/40 p-8 rounded-3xl w-full border border-slate-800 shadow-xl">
          <div className="flex items-center justify-between mb-8 border-b border-slate-800 pb-4">
            <h3 className="text-2xl font-bold text-white">{editId ? 'Edit Sub Agent' : 'Add New Sub Agent'}</h3>
            <button onClick={() => setView('list')} className="text-gray-400 hover:text-white transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              
              {/* Basic Details */}
              <div>
                <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2">Basic Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <input type="text" placeholder="Sub Agent Name *" required className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full" value={formData.sub_agent_name} onChange={e => { setFormData({...formData, sub_agent_name: e.target.value}); setFormErrors({...formErrors, sub_agent_name: null}) }} />
                    {formErrors.sub_agent_name && <p className="text-rose-500 text-xs mt-1">{formErrors.sub_agent_name}</p>}
                  </div>
                  <input type="text" placeholder="Sub Agent Code" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.sub_agent_code} onChange={e => setFormData({...formData, sub_agent_code: e.target.value})} />
                  <Flatpickr
                    options={{ enableTime: true, dateFormat: "Y-m-d\\TH:i" }}
                    placeholder="Collection Date & Time"
                    className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500"
                    value={formData.sub_agent_data_collection_date_and_time}
                    onChange={(dates, dateStr) => setFormData({...formData, sub_agent_data_collection_date_and_time: dateStr})}
                  />
                  <input type="number" placeholder="Discount %" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.sub_agent_discount_perc} onChange={e => setFormData({...formData, sub_agent_discount_perc: e.target.value})} />
                  <input type="text" placeholder="Data Source (Others)" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.sub_agent_data_source_others} onChange={e => setFormData({...formData, sub_agent_data_source_others: e.target.value})} />
                </div>
              </div>

              {/* Address Details */}
              <div>
                <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2">Address Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input type="text" placeholder="Address Line 1" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.sub_agent_address_line1} onChange={e => setFormData({...formData, sub_agent_address_line1: e.target.value})} />
                  <input type="text" placeholder="Address Line 2" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.sub_agent_address_line2} onChange={e => setFormData({...formData, sub_agent_address_line2: e.target.value})} />
                  <input type="text" placeholder="Address Line 3" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.sub_agent_address_line3} onChange={e => setFormData({...formData, sub_agent_address_line3: e.target.value})} />
                  <select 
                    className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" 
                    value={formData.ref_country_id} 
                    onChange={e => setFormData({...formData, ref_country_id: e.target.value})}
                  >
                    <option value="" disabled hidden>Select Country</option>
                    {countriesList.map(c => <option key={c.id} value={c.country_name}>{c.country_name}</option>)}
                  </select>
                  <input type="text" placeholder="State" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.ref_state_id} onChange={e => setFormData({...formData, ref_state_id: e.target.value})} />
                  <input type="text" placeholder="District" className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm" value={formData.ref_district_id} onChange={e => setFormData({...formData, ref_district_id: e.target.value})} />
                  <div>
                    <input 
                      type="text" 
                      placeholder="Pincode" 
                      className="bg-slate-950 border border-slate-800 p-2 rounded-xl text-white text-sm w-full outline-none focus:border-emerald-500" 
                      value={formData.sub_agent_pincode} 
                      onChange={e => {
                        setFormData({...formData, sub_agent_pincode: e.target.value.replace(/\D/g, '').slice(0, 6)});
                        setFormErrors(prev => ({...prev, sub_agent_pincode: null}));
                      }} 
                    />
                    {formErrors.sub_agent_pincode && <p className="text-rose-500 text-xs mt-1 px-1">{formErrors.sub_agent_pincode}</p>}
                  </div>
                </div>
              </div>

              {/* Contact Numbers */}
              <div>
                <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2 flex justify-between">
                  <span>Contact Numbers</span>
                  <button type="button" onClick={addContact} className="text-emerald-400 hover:text-emerald-300 text-xs">+ Add Contact</button>
                </h4>
                <div className="space-y-4">
                  {contacts.map((contact, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center bg-slate-900/50 p-3 rounded-xl border border-slate-800">
                      <input type="text" placeholder="Contact Person" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={contact.contact_person} onChange={e => updateContact(index, 'contact_person', e.target.value)} />
                      <FieldWithAdd type="designation" placeholder="Designation" value={contact.ref_designation_id} onChange={e => updateContact(index, 'ref_designation_id', e.target.value)} />
                      <input type="text" placeholder="Number Type" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={contact.ref_contact_number_type_id} onChange={e => updateContact(index, 'ref_contact_number_type_id', e.target.value)} />
                      <div>
                        <div className="flex bg-slate-950 border border-slate-800 rounded-md overflow-hidden">
                          <select 
                            className="bg-slate-800 text-gray-300 px-1 py-2 flex items-center justify-center text-xs border-r border-slate-700 outline-none w-[60px]"
                            value={contact.contact_country_code || '+91'}
                            onChange={e => updateContact(index, 'contact_country_code', e.target.value)}
                          >
                            {countriesList.map(c => <option key={c.id} value={c.country_code}>{c.country_code}</option>)}
                          </select>
                          <input type="text" placeholder="Contact Number" className="bg-transparent p-2 text-white text-xs w-full outline-none" value={contact.contact_number} onChange={e => { updateContact(index, 'contact_number', e.target.value.replace(/\D/g, '').slice(0, getMobileLengthByCode(contact.contact_country_code || '+91'))); setFormErrors(prev => ({...prev, [`contact_${index}_number`]: null}))}} />
                        </div>
                        {formErrors[`contact_${index}_number`] && <p className="text-rose-500 text-xs mt-1">{formErrors[`contact_${index}_number`]}</p>}
                      </div>
                      <input type="text" placeholder="Extension" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={contact.contact_extension} onChange={e => updateContact(index, 'contact_extension', e.target.value)} />
                      <Flatpickr
                        options={{ enableTime: true, noCalendar: true, dateFormat: "H:i", time_24hr: true }}
                        placeholder="Timing From"
                        className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs w-full outline-none focus:border-emerald-500"
                        value={contact.contact_timing_from}
                        onChange={(dates, dateStr) => updateContact(index, 'contact_timing_from', dateStr)}
                      />
                      <Flatpickr
                        options={{ enableTime: true, noCalendar: true, dateFormat: "H:i", time_24hr: true }}
                        placeholder="Timing To"
                        className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs w-full outline-none focus:border-emerald-500"
                        value={contact.contact_timing_to}
                        onChange={(dates, dateStr) => updateContact(index, 'contact_timing_to', dateStr)}
                      />
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <input type="checkbox" checked={contact.primary_contact} onChange={e => updateContact(index, 'primary_contact', e.target.checked)} />
                        Primary
                        {contacts.length > 1 && (
                          <button type="button" onClick={() => removeContact(index)} className="ml-auto text-rose-500 hover:text-rose-400 font-bold">Remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Email IDs */}
              <div>
                <h4 className="text-emerald-500 font-bold mb-4 border-b border-slate-800 pb-2 flex justify-between">
                  <span>Email IDs</span>
                  <button type="button" onClick={addEmail} className="text-emerald-400 hover:text-emerald-300 text-xs">+ Add Email</button>
                </h4>
                <div className="space-y-4">
                  {emails.map((email, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center bg-slate-900/50 p-3 rounded-xl border border-slate-800">
                      <div className="col-span-2">
                        <input type="email" placeholder="Email ID" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs w-full" value={email.email_id} onChange={e => { updateEmail(index, 'email_id', e.target.value); setFormErrors(prev => ({...prev, [`email_${index}_id`]: null}))}} />
                        {formErrors[`email_${index}_id`] && <p className="text-rose-500 text-xs mt-1">{formErrors[`email_${index}_id`]}</p>}
                      </div>
                      <input type="text" placeholder="Contact Person" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={email.contact_person} onChange={e => updateEmail(index, 'contact_person', e.target.value)} />
                      <input type="text" placeholder="Designation" className="bg-slate-950 border border-slate-800 p-2 rounded-md text-white text-xs" value={email.ref_designation_id} onChange={e => updateEmail(index, 'ref_designation_id', e.target.value)} />
                      <div className="flex items-center gap-2 text-xs text-gray-400 justify-end">
                        <input type="checkbox" checked={email.primary_contact} onChange={e => updateEmail(index, 'primary_contact', e.target.checked)} />
                        Primary
                        {emails.length > 1 && (
                          <button type="button" onClick={() => removeEmail(index)} className="ml-2 text-rose-500 hover:text-rose-400 font-bold">Remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-6 border-t border-slate-800">
                <button type="button" onClick={() => setView('list')} className="w-1/3 bg-slate-800 text-white p-3 rounded-xl font-bold">Cancel</button>
                <button type="submit" className="w-2/3 bg-emerald-500 text-black font-bold p-3 rounded-xl hover:bg-emerald-400">Save Sub Agent</button>
              </div>
            </form>
        </div>
      )}

      {itemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-2">Delete Sub Agent</h3>
            <p className="text-sm text-gray-400 mb-6">Are you sure you want to delete this sub agent? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button 
                type="button"
                onClick={() => setItemToDelete(null)} 
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-bold text-sm transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SubAgentMaster;
